import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, withStyles } from '@material-ui/core';
import Search from '../../shared/search';
import CustomButton from '../../shared/customButton';
import AlertDialog from '../../shared/alertDialog';
import ReadLessMore from '../../shared/readLessMore';
import SurveyActionBar from '../../shared/surveyActionBar';
import GridTable from '../../shared/gridTable';
import DotsMenu from '../../shared/dotsMenu';
import Filters from '../../shared/filters';
import StatusIndicator from '../../shared/statusIndicator';
import SurveyTypeDialog from '../../shared/surveyTypeDialog';
import SendSurveyNotificationDialog from '../../shared/sendSurveyNotificationDialog';
import NotificationCard from '../../shared/notificationCard';
import SurveyType from '../../shared/surveyType';
import ChooseNameDialog from '../../shared/chooseNameDialog';
import CustomFormDrawer from '../../shared/customFormDrawer';
import ActionButton from '../../shared/actionButton';
import UserAvatar from '../../shared/userAvatar';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import SurveyIconInfoTooltip from '../../shared/surveyIconInfoTooltip';
import CustomSelectDialog from '../../shared/customSelectDialog';
import SurveyGroups from '../../shared/surveyGroups';
import SelectField from '../../shared/selectField';
import { ReactComponent as TitleIcon } from '../../../assets/icons/title.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-outlined.svg';
import { ReactComponent as ReminderIcon } from '../../../assets/icons/notifications_none.svg';
import { ReactComponent as ForceCompleteIcon } from '../../../assets/icons/check_circle_outline.svg';
import { ReactComponent as DuplicateIcon } from '../../../assets/icons/content_copy.svg';
import { ReactComponent as ManageGroupIcon } from '../../../assets/icons/playlist_add.svg';
import { ReactComponent as SendIcon } from '../../../assets/icons/send.svg';
import { ReactComponent as RemoveIcon } from '../../../assets/icons/close-icon-dark.svg';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { SURVEY_STATUS_TYPES } from '../../../constants/statuses';
import { USER_BASIC_PERSONAL_INFORMATION } from '../../../constants/people';
import {
  INIT_SORT,
  SURVEY_TYPES,
  CREATE_SURVEY_FIELD_NAMES,
  SURVEY_GROUP_FIELD,
  SURVEY_TITLE_FIELD,
  SURVEYS_GROUP_FIELD,
} from '../../../constants/survey';
import {
  PARAMS,
  SURVEYS_DEFAULT_PARAMS,
  SURVEY_GROUPS_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
} from '../../../constants/pages';
import { sticky } from '../../../constants/helperCssRules';
import {
  API_SURVEYS,
  API_SURVEY_GROUPS,
  API_QUESTIONNAIRES,
  api_survey,
  api_survey_send_reminder,
  api_survey_group,
  api_survey_group_duplicate,
  api_survey_activate,
  api_survey_group_items_reorder,
  api_survey_duplicate,
  api_questionnaire,
} from '../../../constants/apiRoutes';
import {
  showSuccessMessage,
  parseDuplicateParameters,
  parseQueryParams,
  hasNextPage,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import {
  isObjectEmpty,
  isArray,
  isArrayEmpty,
  trimString,
  getItemById,
  getEmployees,
  getAssociates,
  getObjectToNumberArray,
  getActiveUsers,
} from '../../../utility/helpers';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';
import http from '../../../utility/http';
import {
  isDateInFuture,
  addNumberOfDays,
  isDateInPast,
  getStartOfDay,
} from '../../../utility/dateUtils';
import { prepareActiveAttributes } from '../../../utility/attribute';
import { hasAuthPermissionsCheck } from '../../../utility/permissions';
import {
  prepareSurveyViewOptions,
  prepareSurveyTypes,
  prepareSurveysPageFilters,
  prepareSurveysHeaderActions,
  prepareSurveysTableHeaders,
  prepareSurveysTableData,
  prepareSurveyGroupsData,
  prepareCreateSurveyInitialData,
  prepareCreateSurveyColumns,
  prepareSurveyDataForCreation,
  prepareNoSurveyResultsData,
  prepareSurveyDeleteTranslations,
} from '../../../utility/survey';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import { ATTRIBUTE_TYPES_MAP } from '../attributesPage/config';

const styles = ({ breakpoints, palette: { primary }, spacing }) => ({
  stickyHeader: {
    ...sticky(primary.white, 105),
  },
  descriptionWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing(8, 0, 6, 0),
    minHeight: 88,
  },
  descriptionReadMoreWrapper: {
    alignItems: 'flex-start',
  },
  pendingSurveysBar: {
    flexShrink: 0,
    marginLeft: 'auto',
  },
  search: {
    width: 300,
  },
  actionButton: {
    marginLeft: spacing(2),
  },
  table: {
    '& .grid-table-header': {
      ...sticky(primary.bluish8, 249, 998),
    },
    '& .grid-table-header$readMoreActive': {
      top: 271,
    },
    '& .row-cell-value': {
      color: `${primary.bluish1} !important`,
    },
    '& .status-cell': {
      padding: 0,
    },
    '& .report-cell': {
      padding: 0,
    },
    '& .actions-dots-menu': {
      padding: spacing(3, 1),
      overflow: 'visible',

      [breakpoints.up('xLg')]: {
        padding: spacing(3.5, 3),
      },
    },
  },
  cell: {
    color: primary.bluish1,
  },
  readMoreActive: {},
  filtersAndPagerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: spacing(4),
  },
  filterSection: {
    display: 'flex',
    alignItems: 'center',
  },
  rightSide: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  selectedFilters: {
    marginLeft: spacing(2),
    '& button': { marginBottom: 0 },
    '& div': {
      '& div': { marginBottom: 0 },
    },
  },
  completedValue: {
    marginLeft: spacing(2),
  },
  reportTooltip: {
    fontFamily: 'ProximaNova-Bold',
  },
  statusAndCompletedWrapper: {
    display: 'flex',
    alignItems: 'center',
    padding: spacing(4, 2),
    [breakpoints.up('xLg')]: {
      padding: spacing(4),
    },
  },
  fillInlineButtonWrapper: {
    padding: spacing(2.5, 2),
    [breakpoints.up('xLg')]: {
      padding: spacing(2.5, 4),
    },
  },
  createSurveyHeader: {
    padding: spacing(5, 8, 5, 5),
    [breakpoints.up('xLg')]: {
      padding: spacing(5, 8, 5, 9),
    },
  },
  surveyType: {
    '& .MuiPaper-root': {
      minHeight: 238,
      '& > *': {
        minHeight: 300,
      },
    },
  },
  columnContent: {
    padding: 0,
  },
  columns: {
    gridTemplateColumns: 'repeat(2, minmax(340px, 1fr)) minmax(0, 2fr)',
    [breakpoints.up('xLg')]: {
      gridTemplateColumns: 'repeat(2, minmax(372px, 1fr)) minmax(0, 2fr)',
    },

    '& .mutualColumnContent, & .peopleColumnContent': {
      padding: spacing(6, 6, 0),
      [breakpoints.up('xLg')]: {
        padding: spacing(8, 9, 0, 9),
      },
    },

    '& .selectedUsersScrollY': {
      right: -9,
      [breakpoints.up('xLg')]: {
        right: -12,
      },
    },

    '& .mutualColumn': {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gridColumnGap: spacing(2),
      gridTemplateAreas: `
        "type type type"
        "title title title"
        "anonymous anonymous anonymous"
        "statistical statistical statistical"
        "expiryDate expiryDate expiryTime"
        "email email email"
        "emailDescription emailDescription emailDescription"
        "schedule schedule schedule"
        "scheduleDate scheduleDate scheduleTime"
        "autoReminder autoReminder autoReminder"
        "autoReminderOptions autoReminderOptions autoReminderOptions"
        "autoShare autoShare autoShare"
        "autoShareTitle autoShareTitle autoShareTitle"
        "summary summary summary"
        "autoShareTo autoShareTo autoShareTo"
      `,
      '& .type': {
        gridArea: 'type',
      },
      '& .title': {
        gridArea: 'title',
      },
      '& .anonymous': {
        gridArea: 'anonymous',
      },
      '& .statistical': {
        gridArea: 'statistical',
        marginBottom: 0,
      },
      '& .email': {
        gridArea: 'email',
        marginBottom: spacing(4),
      },
      '& .emailDescription': {
        gridArea: 'emailDescription',
        marginBottom: 0,
      },
      '& .schedule': {
        gridArea: 'schedule',
        marginBottom: spacing(4),
      },
      '& .scheduleDate': {
        position: 'relative',
        gridArea: 'scheduleDate',
        marginBottom: 0,
        '&::after': {
          width: 'calc(100% + 8px)',
        },
      },
      '& .scheduleTime': {
        gridArea: 'scheduleTime',
        marginBottom: 0,
        '& .scheduleTimeRoot': {
          zIndex: 97,
        },
      },
      '& .autoReminder': {
        gridArea: 'autoReminder',
        marginBottom: spacing(4),
      },
      '& .autoReminderOptions': {
        gridArea: 'autoReminderOptions',
        marginBottom: spacing(4),
        '& .autoReminderCheckbox': {
          paddingTop: 0,
        },
      },
      '& .expiryDate': {
        gridArea: 'expiryDate',
        marginBottom: 0,
        '&::after': {
          width: 'calc(100% + 8px)',
        },
      },
      '& .expiryTime': {
        gridArea: 'expiryTime',
        paddingTop: spacing(6.5),
        marginBottom: 0,
        '& .expiryTimeRoot': {
          zIndex: 97,
        },
      },
      '& .autoShare': {
        gridArea: 'autoShare',
        marginBottom: spacing(4),
      },
      '& .autoShareTitle': {
        gridArea: 'autoShareTitle',
        marginBottom: spacing(4),
      },
      '& .summary': {
        gridArea: 'summary',
      },
      '& .autoShareTo': {
        gridArea: 'autoShareTo',
        zIndex: 97,
      },
    },

    '& .peopleColumn': {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gridColumnGap: spacing(2),
      '& > *': {
        gridColumn: '1 / -1',
      },
      '& .attributes, & .questionnaire': {
        alignSelf: 'start',
      },
      '& .attributes': {
        gridColumn: 1,
      },
      '& .questionnaire': {
        gridColumn: 2,
        display: 'flex',
        alignItems: 'center',
        marginTop: spacing(9),
        marginBottom: 0,
      },
      '& .questionnaireSwitch': {
        width: 'auto',
        '& > div': {
          width: 'auto',
        },
      },
    },

    '& .preview-wrapper-attributes, & .preview-wrapper-custom': {
      backgroundColor: primary.bluish9,
      overflow: 'hidden',
      '& .preview-column': {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      },
    },
    '& .preview-wrapper-attributes': {
      overflow: 'visible',
      '& .preview-column-contentWrapper-attributes': {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'visible',
        padding: spacing(6, 5, 4, 5),
        [breakpoints.up('xLg')]: {
          padding: spacing(6, 9, 4, 9),
        },
      },
    },
    '& .preview-wrapper-custom': {
      '& .preview-column-contentWrapper-custom': {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        padding: spacing(5, 5, 4, 5),
        [breakpoints.up('xLg')]: {
          padding: spacing(8, 9, 4, 9),
        },
      },
    },
  },
  column: {},
  surveyTitle: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '100%',
  },
  title: {
    width: 'auto',
  },
  infoIcon: {
    flexShrink: 0,
    '& svg': {
      width: 16,
      height: 16,
      marginRight: 0,
    },
  },
  anonymous: {
    display: 'block',
    marginTop: spacing(0.5),
    '& svg': {
      width: 16,
      height: 14,
    },
  },
  fillSurveyButton: {
    border: `1px solid ${primary.bluish1}`,
    borderRadius: 48,
    color: primary.bluish1,
    fontFamily: 'ProximaNova-Bold',
    padding: spacing(2, 4),
    maxHeight: 32,
  },
  buttonIconWrapper: {
    marginLeft: 0,
    marginRight: spacing(1),
  },
  buttonIcon: {
    height: 16,
    width: 16,
  },
  organizationUser: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 14,
    lineHeight: '18px',
    width: '100%',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  viewMenu: {
    zIndex: 1000,
  },
  surveyTypeTooltip: {
    cursor: 'pointer',
  },
  typeIcon: {
    marginRight: 0,
  },
  selectMenu: {
    maxHeight: '338px !important',
  },
  selectMenuList: {
    maxHeight: '304px !important',
  },
});

const {
  PAGE,
  SEARCH,
  ORDERING,
  TYPE,
  REVIEWERS,
  CREATOR,
  CREATED_FOR,
  STATUS,
} = PARAMS;

class SurveysPage extends PureComponent {
  state = {
    isInitialLoad: true,
    isLoading: false,
    [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE],
    filters: {
      [TYPE]: [],
      [REVIEWERS]: [],
      [CREATOR]: [],
      [CREATED_FOR]: [],
      [STATUS]: [],
    },
    [SEARCH]: '',
    [ORDERING]: INIT_SORT,
    isReadMore: true,
    isSurveyTypeDialogOpened: false,
    selectedSurveyType: null,
    isCreateSurveyOpened: false,
    isSendSurveyNotificationOpened: false,
    surveyData: null,
    predefinedSurveyData: {},
    isSurveyTitleDialogOpened: false,
    isGroupDialogOpened: false,
    isManageSurveyGroupDialogOpened: false,
    survey: null,
    surveyId: null,
    isGroupView: false,
    surveyGroup: null,
    isDeleteSurveyGroupOpened: false,
    isDuplicateGroupOpened: false,
    isDuplicateSurveyOpened: false,
    isRemoveFromGroupOpened: false,
  };

  componentDidMount() {
    this.getInitialData();
  }

  componentDidUpdate() {
    const { location, navigate } = this.props;
    const { page, filters, ordering, search, isGroupView } = this.state;

    const params = {
      page,
      ...(isGroupView ? {} : { [ORDERING]: ordering.sortKey }),
      ...(isGroupView ? {} : filters),
      ...(search ? { search } : {}),
    };
    const query = parseDuplicateParameters(params);

    if (decodeURIComponent(location.search) !== `?${query}`) {
      navigate(`/surveys/?${query}`, { replace: true, state: { isGroupView } });
    }
  }

  componentWillUnmount() {
    const {
      clearSurveys,
      clearPendingSurveys,
      clearAllUsers,
      clearAttributesWithQuestions,
      clearAttribute,
      clearSurvey,
      clearSurveyGroups,
      clearAllSurveyGroups,
    } = this.props;

    clearSurveys();
    clearPendingSurveys();
    clearAllUsers();
    clearAttributesWithQuestions();
    clearAttribute();
    clearSurvey();
    clearSurveyGroups();
    clearAllSurveyGroups();
  }

  getInitialData = () => {
    const {
      translations,
      accessControl,
      location,
      getAttributesWithQuestions,
      getPendingSurveys,
      getAllUsers,
      getAllSurveyGroups,
    } = this.props;
    const { state, search } = location;
    const [canViewAllSurveys] = hasAuthPermissionsCheck(
      [PERMISSION_CODES.SURVEYS_VIEW_ALL],
      accessControl
    );

    const isGroupView = canViewAllSurveys && !!state?.isGroupView;
    const surveyData = location?.state?.surveyData;
    const columns = prepareSurveysTableHeaders(translations.columns);
    const params = parseQueryParams(
      search,
      isGroupView
        ? PAGE_WHITELISTED_PARAMS.SURVEY_SECTIONS
        : PAGE_WHITELISTED_PARAMS.SURVEYS
    );

    if (surveyData) {
      return this.setState(
        {
          selectedSurveyType: surveyData[CREATE_SURVEY_FIELD_NAMES.SURVEY_TYPE],
          isCreateSurveyOpened: true,
          predefinedSurveyData: surveyData,
        },
        () =>
          Promise.all([
            this.handlePageChange(),
            getAllUsers(),
            getAttributesWithQuestions(),
            getPendingSurveys(),
            ...(canViewAllSurveys ? [getAllSurveyGroups()] : []),
          ]).then(() => {
            this.setState({ isInitialLoad: false });
          })
      );
    }

    if (!isObjectEmpty(params)) {
      const [ascending, descending] = isGroupView
        ? []
        : (params[ORDERING] || INIT_SORT.sortKey).split('-');
      const isAscending = !!ascending;

      return this.setState(
        {
          isGroupView,
          ...(!isGroupView
            ? {
                [ORDERING]: {
                  column: getItemById(
                    columns,
                    isAscending ? ascending : descending,
                    'sortAs'
                  )?.rowKey,
                  asc: isAscending,
                  sortKey: params[ORDERING] || INIT_SORT.sortKey,
                },
              }
            : {}),
          [SEARCH]: params[SEARCH] ? decodeURIComponent(params[SEARCH]) : '',
          filters: {
            [STATUS]: params[STATUS] ? [params[STATUS]] : [],
            [TYPE]: params[TYPE] ? [params[TYPE]] : [],
            [REVIEWERS]: params[REVIEWERS]
              ? [
                  ...(isArray(params[REVIEWERS])
                    ? params[REVIEWERS]
                    : [params[REVIEWERS]]),
                ]
              : [],
            [CREATOR]: params[CREATOR]
              ? [
                  ...(isArray(params[CREATOR])
                    ? params[CREATOR]
                    : [params[CREATOR]]),
                ]
              : [],
            [CREATED_FOR]: params[CREATED_FOR]
              ? [
                  ...(isArray(params[CREATED_FOR])
                    ? params[CREATED_FOR]
                    : [params[CREATED_FOR]]),
                ]
              : [],
          },
        },
        () => {
          return Promise.all([
            this.handlePageChange(false),
            getAllUsers(),
            getAttributesWithQuestions(),
            getPendingSurveys(),
            ...(canViewAllSurveys ? [getAllSurveyGroups()] : []),
          ]).then(() => {
            this.setState({ isInitialLoad: false });
          });
        }
      );
    }

    return Promise.all([
      this.handlePageChange(),
      getAllUsers(),
      getAttributesWithQuestions(),
      getPendingSurveys(),
      ...(canViewAllSurveys ? [getAllSurveyGroups()] : []),
    ]).then(() => {
      this.setState({ isInitialLoad: false });
    });
  };

  handlePageChange = (isLoadMore = false) => {
    const { getSurveys, getSurveyGroups } = this.props;
    const { page, filters, ordering, search, isGroupView } = this.state;
    const params = {
      page,
      ...(isGroupView ? {} : { [ORDERING]: ordering.sortKey }),
      ...(isGroupView ? {} : filters),
      ...(search ? { search } : {}),
    };

    return isGroupView
      ? getSurveyGroups(isLoadMore, params)
      : getSurveys(isLoadMore, params);
  };

  onSearch = text => {
    const searchTerm = trimString(text);

    this.setState(
      {
        [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE],
        [SEARCH]: searchTerm,
        isLoading: true,
      },
      () =>
        this.handlePageChange().then(() => this.setState({ isLoading: false }))
    );
  };

  onSort = newOrdering => {
    this.setState(
      { [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE], [ORDERING]: newOrdering },
      () => this.handlePageChange()
    );
  };

  onLoadMore = () => {
    return this.setState(
      prevState => ({ ...prevState, [PAGE]: prevState[PAGE] + 1 }),
      () => this.handlePageChange(true)
    );
  };

  handleApplyFilters = filters =>
    this.setState(
      { [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE], filters, isLoading: true },
      () =>
        this.handlePageChange().then(() => this.setState({ isLoading: false }))
    );

  handleSendReminder = surveyId => {
    const { translations } = this.props;
    const params = {
      client_url: `${window.location.origin}/fill-survey`,
      group_id: surveyId,
    };

    http.post(api_survey_send_reminder(surveyId), params).then(() => {
      showSuccessMessage(translations.reminderSent);
    });
  };

  handleFillInlineSurvey = async surveyId => {
    const { navigate, getTrackingById, setIsInlineSurvey } = this.props;
    const { data } = await getTrackingById(surveyId);
    const surveyToken = data && data.tracking_token ? data.tracking_token : '';

    if (surveyToken) {
      setIsInlineSurvey(true);

      navigate(`/fill-survey/${surveyToken}`);
    }
  };

  handleOpenSurveyTypeDialog = (e, surveyGroup = null) =>
    this.setState({
      isSurveyTypeDialogOpened: true,
      surveyGroup,
    });

  handleCloseSurveyType = () =>
    this.setState({
      isSurveyTypeDialogOpened: false,
      selectedSurveyType: null,
      surveyGroup: null,
    });

  handleSaveSurveyType = () => {
    const { selectedSurveyType } = this.state;

    return this.setState({
      isSurveyTypeDialogOpened: false,
      isCreateSurveyOpened: true,
      selectedSurveyType: selectedSurveyType || SURVEY_TYPES.SELF,
    });
  };

  handleSelectSurveyType = surveyType => () => {
    const { selectedSurveyType } = this.state;

    if (selectedSurveyType !== surveyType) {
      this.setState({ selectedSurveyType: surveyType });
    }
  };

  handleChangeSurveyType = surveyType =>
    this.setState({
      selectedSurveyType: surveyType,
      predefinedSurveyData: {},
    });

  handleCloseCreateSurveyDrawer = () => {
    const { clearAttribute } = this.props;

    clearAttribute();
    this.setState({
      isCreateSurveyOpened: false,
      selectedSurveyType: null,
      predefinedSurveyData: {},
      surveyGroup: null,
    });
  };

  handleCreateSurveySave = values => {
    this.setState({ surveyData: values, isSendSurveyNotificationOpened: true });

    return Promise.resolve({ shouldResetForm: false });
  };

  handleCloseSurveyNotificationDialog = () =>
    this.setState({ isSendSurveyNotificationOpened: false, surveyData: null });

  handleFillInlineChange = isFillInline => {
    const { FILL_INLINE } = CREATE_SURVEY_FIELD_NAMES;

    this.setState(prevState => ({
      surveyData: { ...prevState.surveyData, [FILL_INLINE]: isFillInline },
    }));
  };

  handleCreateSurvey = async (e, isDraftCreation = false) => {
    const { translations, navigate, setIsInlineSurvey, clearAttribute } =
      this.props;
    const { surveyData, isGroupView, predefinedSurveyData } = this.state;
    const { SURVEY_TYPE, FILL_INLINE, HAS_QUESTIONNAIRE, QUESTIONNAIRE } =
      CREATE_SURVEY_FIELD_NAMES;
    const surveyType = surveyData[SURVEY_TYPE];
    const isInlineSurvey = surveyData[FILL_INLINE];
    const hasQuestionnaire = surveyData[HAS_QUESTIONNAIRE];
    const params = prepareSurveyDataForCreation(
      surveyData,
      isGroupView,
      predefinedSurveyData.isDraftActivation,
      isDraftCreation,
      surveyData?.hasAutoShare
    );

    if (isInlineSurvey && !isDraftCreation) {
      setIsInlineSurvey(true);
    }

    if (hasQuestionnaire) {
      if (predefinedSurveyData?.isDraftActivation) {
        await http.patch(
          api_questionnaire(predefinedSurveyData[QUESTIONNAIRE].id),
          params[QUESTIONNAIRE]
        );
        params[QUESTIONNAIRE] = predefinedSurveyData[QUESTIONNAIRE].id;
      } else {
        const { data } = await http.post(
          API_QUESTIONNAIRES,
          params[QUESTIONNAIRE]
        );
        params[QUESTIONNAIRE] = data.id;
      }
    }

    return http
      .post(
        predefinedSurveyData.isDraftActivation
          ? api_survey_activate(predefinedSurveyData.id)
          : `${API_SURVEYS}${surveyType.toLowerCase()}/`,
        params
      )
      .then(
        ({ data }) => {
          const { ComposeNewSurvey } = EVENT_ACTION_TYPES;
          tagManagerDataLayer(
            ComposeNewSurvey.action,
            ComposeNewSurvey.name,
            params.title
          );
          showSuccessMessage(translations.successMessages.created);
          this.setState(
            {
              [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE],
              isSendSurveyNotificationOpened: false,
              isCreateSurveyOpened: false,
              selectedSurveyType: null,
              surveyData: null,
              surveyGroup: null,
              predefinedSurveyData: {},
            },
            () => {
              clearAttribute();
              if (isInlineSurvey && !isDraftCreation) {
                navigate(`/fill-survey/${data.tracking_token}`);
              } else {
                this.handlePageChange();
              }
            }
          );
        },
        () => {
          clearAttribute();
          this.setState({
            selectedSurveyType: null,
            isCreateSurveyOpened: false,
            isSendSurveyNotificationOpened: false,
            surveyData: null,
            surveyGroup: null,
            predefinedSurveyData: {},
          });
        }
      );
  };

  handleDeleteSurvey = () => {
    const { setDialogVisibility, translations } = this.props;
    const { survey } = this.state;

    http.delete(api_survey(survey.id)).then(() => {
      this.setState(
        {
          [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE],
          survey: null,
        },
        () => {
          showSuccessMessage(translations.surveyDeleteDialog.deleteSuccess);
          setDialogVisibility({
            dialogName: 'forceDeleteSurveyDialog',
            opened: false,
          });
          this.handlePageChange();
        }
      );
    });
  };

  handleForceCompleteSurvey = () => {
    const { editSurvey, setDialogVisibility, translations } = this.props;
    const { survey } = this.state;

    return editSurvey(survey.id, {
      [CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE]: getStartOfDay(new Date()),
    }).then(() => {
      this.setState({ survey: null }, () => {
        showSuccessMessage(translations.forceCompleteDialog.completeSuccess);
        setDialogVisibility({
          dialogName: 'forceCompleteSurveyDialog',
          opened: false,
        });
        this.handlePageChange();
      });
    });
  };

  handleToggleReadMore = () => {
    this.setState(prevState => ({
      isReadMore: !prevState.isReadMore,
    }));
  };

  handleToggleSurveyTitleDialog = survey =>
    this.setState(prevState => ({
      isSurveyTitleDialogOpened: !prevState.isSurveyTitleDialogOpened,
      survey,
    }));

  handleChangeSurveyTitle = surveyTitle => {
    const { translations } = this.props;
    const { survey } = this.state;

    return http
      .patch(api_survey(survey.id), { title: surveyTitle })
      .then(() => {
        showSuccessMessage(translations.renameSurveyDialog.success);
        return this.setState(
          {
            [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE],
            isSurveyTitleDialogOpened: false,
            survey: null,
          },
          this.handlePageChange
        );
      });
  };

  handleActivateSurvey = async (surveyId, isDraftActivation = false) => {
    const { isGroupView } = this.state;

    const { data } = await http.get(api_survey(surveyId));

    const {
      id,
      title,
      attributes,
      questionnaire,
      created_for,
      is_statistical,
      scheduled_for,
      is_anonymous,
      reviewers,
      message,
      type,
      group,
      status,
      pre_shared,
    } = data;

    let questionnaireData = null;
    if (questionnaire?.id) {
      const { data: questionnaireResponse } = await http.get(
        api_questionnaire(questionnaire.id)
      );
      questionnaireData = questionnaireResponse;
    }

    const predefinedData = {
      id,
      status,
      isDraftActivation,
      [CREATE_SURVEY_FIELD_NAMES.TITLE]: title,
      [CREATE_SURVEY_FIELD_NAMES.ATTRIBUTES]:
        prepareActiveAttributes(attributes),
      [CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE]: !!questionnaire?.id,
      [CREATE_SURVEY_FIELD_NAMES.QUESTIONNAIRE]: questionnaireData,
      [CREATE_SURVEY_FIELD_NAMES.CREATED_FOR]: getActiveUsers(created_for),
      [CREATE_SURVEY_FIELD_NAMES.IS_STATISTICAL]: is_statistical,
      [CREATE_SURVEY_FIELD_NAMES.IS_SCHEDULE]: Boolean(scheduled_for),
      [CREATE_SURVEY_FIELD_NAMES.IS_ANONYMOUS]: is_anonymous,
      [CREATE_SURVEY_FIELD_NAMES.REVIEWERS]: getActiveUsers(reviewers),
      [CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_SHARE]:
        !!pre_shared?.pre_shared_report,
      [CREATE_SURVEY_FIELD_NAMES.SHARE_TO]: pre_shared?.pre_shared_users,
      [CREATE_SURVEY_FIELD_NAMES.DESCRIPTION]:
        pre_shared?.pre_shared_description || '',
      [CREATE_SURVEY_FIELD_NAMES.HAS_EMAIL_MESSAGE]: true,
      [CREATE_SURVEY_FIELD_NAMES.EMAIL_MESSAGE]: message || '',
      ...(isGroupView || isDraftActivation
        ? { [CREATE_SURVEY_FIELD_NAMES.GROUP]: group }
        : {}),
    };
    this.setState({
      isCreateSurveyOpened: true,
      selectedSurveyType: type,
      predefinedSurveyData: predefinedData,
    });
  };

  handleOpenSurveyDuplicate = async surveyId => {
    this.setState({ isDuplicateSurveyOpened: true, surveyId });
  };

  handleCloseSurveyDuplicate = () =>
    this.setState({ isDuplicateSurveyOpened: false, surveyId: null });

  handleSaveSurveyDuplicate = async () => {
    const { translations } = this.props;
    const { surveyId } = this.state;

    try {
      await http.post(api_survey_duplicate(surveyId));
      showSuccessMessage(translations.surveyDuplicateDialog.duplicateSuccess);
      this.setState(
        {
          [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE],
        },
        () => {
          this.handlePageChange();
        }
      );
    } finally {
      this.handleCloseSurveyDuplicate();
    }
  };

  handleOpenGroupDialog = (e, surveyGroup = null) =>
    this.setState({ isGroupDialogOpened: true, surveyGroup });

  handleCloseGroupDialog = () =>
    this.setState({ isGroupDialogOpened: false, surveyGroup: null });

  handleSaveGroup = groupName => {
    const { translations, getAllSurveyGroups } = this.props;
    const { surveyGroup } = this.state;

    if (surveyGroup) {
      return http
        .patch(api_survey_group(surveyGroup.id), { title: groupName })
        .then(() => {
          this.setState(
            {
              [PAGE]: SURVEY_GROUPS_DEFAULT_PARAMS[PAGE],
              isGroupDialogOpened: false,
              surveyGroup: null,
            },
            () => {
              showSuccessMessage(translations.manageGroupDialog.success.edit);
              getAllSurveyGroups();
              this.handlePageChange();
            }
          );
        });
    }

    return http
      .post(API_SURVEY_GROUPS, { title: groupName, surveys: [] })
      .then(() => {
        this.setState(
          {
            [PAGE]: SURVEY_GROUPS_DEFAULT_PARAMS[PAGE],
            isGroupDialogOpened: false,
          },
          () => {
            showSuccessMessage(translations.manageGroupDialog.success.create);
            this.handlePageChange();
          }
        );
      });
  };

  handleOpenGroupDuplicate = async surveyGroup =>
    this.setState({ isDuplicateGroupOpened: true, surveyGroup });

  handleCloseGroupDuplicate = () =>
    this.setState({ isDuplicateGroupOpened: false, surveyGroup: null });

  handleDuplicateGroup = () => {
    const { translations, getAllSurveyGroups } = this.props;
    const { surveyGroup } = this.state;

    return http.post(api_survey_group_duplicate(surveyGroup.id)).then(
      () => {
        this.setState(
          {
            [PAGE]: SURVEY_GROUPS_DEFAULT_PARAMS[PAGE],
            isDuplicateGroupOpened: false,
            surveyGroup: null,
          },
          () => {
            showSuccessMessage(
              translations.surveyGroupDuplicateDialog.duplicateSuccess
            );
            this.handlePageChange();
            getAllSurveyGroups();
          }
        );
      },
      () => {
        this.setState({
          [PAGE]: SURVEY_GROUPS_DEFAULT_PARAMS[PAGE],
          isDuplicateGroupOpened: false,
          surveyGroup: null,
        });
      }
    );
  };

  handleOpenGroupDelete = surveyGroup =>
    this.setState({ isDeleteSurveyGroupOpened: true, surveyGroup });

  handleCloseGroupDelete = () =>
    this.setState({ isDeleteSurveyGroupOpened: false, surveyGroup: null });

  handleDeleteGroup = () => {
    const { translations, getAllSurveyGroups } = this.props;
    const { surveyGroup } = this.state;

    return http.delete(api_survey_group(surveyGroup.id)).then(() => {
      this.setState(
        {
          [PAGE]: SURVEY_GROUPS_DEFAULT_PARAMS[PAGE],
          isDeleteSurveyGroupOpened: false,
          surveyGroup: null,
        },
        () => {
          showSuccessMessage(
            translations.surveyGroupDeleteDialog.deleteSuccess
          );
          this.handlePageChange();
          getAllSurveyGroups();
        }
      );
    });
  };

  handleOpenSurveyGroupDialog = (survey = null) =>
    this.setState({ isManageSurveyGroupDialogOpened: true, survey });

  handleCloseSurveyGroupDialog = () =>
    this.setState({ isManageSurveyGroupDialogOpened: false, survey: null });

  handleSaveSurveyGroup = groupId => {
    const { translations } = this.props;
    const { survey } = this.state;

    return http.patch(api_survey(survey.id), { group: groupId }).then(() => {
      this.setState(
        {
          [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE],
          isManageSurveyGroupDialogOpened: false,
          survey: null,
        },
        () => {
          showSuccessMessage(
            survey.group
              ? translations.manageGroupItemsDialog.success.edit
              : translations.manageGroupItemsDialog.success.create
          );
          this.handlePageChange();
        }
      );
    });
  };

  handleOpenRemoveFromGroupDialog = survey =>
    this.setState({ isRemoveFromGroupOpened: true, survey });

  handleCloseRemoveFromGroupDialog = () =>
    this.setState({ isRemoveFromGroupOpened: false, survey: null });

  handleSaveRemoveFromGroup = () => {
    const { translations, deleteSurveyFromGroup } = this.props;
    const { isGroupView, survey } = this.state;

    return http.patch(api_survey(survey.id), { group: null }).then(() => {
      if (isGroupView) {
        deleteSurveyFromGroup({
          groupId: survey[CREATE_SURVEY_FIELD_NAMES.GROUP],
          survey,
        });

        return this.setState(
          {
            isRemoveFromGroupOpened: false,
            survey: null,
          },
          () => {
            showSuccessMessage(translations.removeFromGroupDialog.success);
          }
        );
      }

      return this.setState(
        {
          [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE],
          isRemoveFromGroupOpened: false,
          survey: null,
        },
        () => {
          showSuccessMessage(translations.removeFromGroupDialog.success);
          this.handlePageChange();
        }
      );
    });
  };

  handleReorderGroupItems = (groupId, reorderedSurveys) => {
    const { setGroupSurveys } = this.props;
    const surveyIds = getObjectToNumberArray(reorderedSurveys);

    setGroupSurveys({ id: groupId, surveyIds });

    return http.post(api_survey_group_items_reorder(groupId), {
      order: surveyIds,
    });
  };

  handleChangeView = view =>
    this.setState(
      { isGroupView: view, [PAGE]: SURVEYS_DEFAULT_PARAMS[PAGE] },
      this.handlePageChange
    );

  getMenuItems = (survey, canManageAll, canManageCreated) => {
    const { translations, allSurveyGroups, setDialogVisibility } = this.props;
    const hasGroups = !isArrayEmpty(allSurveyGroups);
    const hasGroup = !!survey[CREATE_SURVEY_FIELD_NAMES.GROUP];
    const isDraft = survey.status === SURVEY_STATUS_TYPES.DRAFT;

    return [
      {
        type: 'reminder',
        label: translations.menuItemLabels.reminder,
        shouldItemRender: () =>
          survey.status === SURVEY_STATUS_TYPES.ACTIVE &&
          (canManageAll || canManageCreated),
        action: () => this.handleSendReminder(survey.id),
        icon: ReminderIcon,
      },
      {
        type: 'sendSurvey',
        label: translations.menuItemLabels.reviewAndSend,
        shouldItemRender: () => isDraft && (canManageAll || canManageCreated),
        action: () => this.handleActivateSurvey(survey.id, true),
        icon: SendIcon,
      },
      {
        type: 'edit',
        label: translations.menuItemLabels.renameSurvey,
        shouldItemRender: () => canManageAll || canManageCreated,
        action: () => this.handleToggleSurveyTitleDialog(survey),
        icon: TitleIcon,
      },
      {
        type: 'manageGroup',
        label: translations.menuItemLabels.manageGroup,
        shouldItemRender: () => canManageAll && hasGroups,
        action: () => this.handleOpenSurveyGroupDialog(survey),
        icon: ManageGroupIcon,
      },
      {
        type: 'removeGroup',
        label: translations.menuItemLabels.removeGroup,
        shouldItemRender: () => canManageAll && hasGroup,
        action: () => this.handleOpenRemoveFromGroupDialog(survey),
        icon: RemoveIcon,
      },
      {
        type: 'duplicate',
        label: translations.menuItemLabels.duplicate,
        shouldItemRender: () => canManageAll || canManageCreated,
        action: () => this.handleOpenSurveyDuplicate(survey.id),
        icon: DuplicateIcon,
      },
      {
        type: 'forceComplete',
        label: translations.menuItemLabels.forceComplete,
        shouldItemRender: () =>
          (canManageAll || canManageCreated) &&
          survey.status !== SURVEY_STATUS_TYPES.COMPLETED &&
          survey.status !== SURVEY_STATUS_TYPES.SCHEDULED &&
          isDateInPast(addNumberOfDays(getStartOfDay(survey.sent_at), 2)) &&
          isDateInFuture(survey.expire_at),

        action: () => {
          this.setState({ survey }, () => {
            setDialogVisibility({
              dialogName: 'forceCompleteSurveyDialog',
              opened: true,
            });
          });
        },
        icon: ForceCompleteIcon,
      },
      {
        type: 'delete',
        label: translations.menuItemLabels.delete,
        shouldItemRender: () => canManageAll || canManageCreated,
        action: () => {
          this.setState({ survey }, () => {
            setDialogVisibility({
              dialogName: 'forceDeleteSurveyDialog',
              opened: true,
            });
          });
        },
        icon: DeleteIcon,
      },
    ];
  };

  renderTitle = survey => {
    const { classes, translations } = this.props;

    return (
      <div className={classes.surveyTitle}>
        <TextBoxWithTooltip
          className={classes.title}
          text={survey.title}
          variant="subtitle1"
        />
        {!survey.is_statistical && (
          <SurveyIconInfoTooltip
            className={classes.infoIcon}
            tooltipText={translations.oneTimeSurvey}
            isOneTimeSurvey
          />
        )}
        {survey.is_anonymous && (
          <SurveyIconInfoTooltip
            className={classNames(classes.anonymous, classes.infoIcon)}
            tooltipText={translations.anonymousSurvey}
          />
        )}
      </div>
    );
  };

  renderActions = (survey, canManageAll, canManageCreated) => {
    const { classes, translations } = this.props;
    const isDraft =
      survey[CREATE_SURVEY_FIELD_NAMES.STATUS] === SURVEY_STATUS_TYPES.DRAFT;

    return (
      <div className={classes.actions}>
        {!isDraft && (
          <ActionButton
            type={ACTION_BUTTON_TYPES.REPORT}
            tooltipText={translations.viewReport}
            linkProps={{
              to: `/surveys/${survey.id}/report`,
            }}
            isLink
          />
        )}
        {(canManageAll || canManageCreated) && (
          <DotsMenu
            menuItems={this.getMenuItems(
              survey,
              canManageAll,
              canManageCreated
            )}
          />
        )}
      </div>
    );
  };

  renderStatusAndCompleted = (survey, status, type, isDraft) => {
    const { classes, translations } = this.props;
    const { id, completed, review_link_available } = survey;
    const { icon: Icon } = type;

    if (review_link_available) {
      return (
        <div className={classes.fillInlineButtonWrapper}>
          <CustomButton
            className={classes.fillSurveyButton}
            classes={{ startIcon: classes.buttonIconWrapper }}
            type="addWithTextRounded"
            startIcon={<Icon className={classes.buttonIcon} />}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              return this.handleFillInlineSurvey(id);
            }}
          >
            {translations.menuItemLabels.fillSurvey}
          </CustomButton>
        </div>
      );
    }

    return (
      <div className={classes.statusAndCompletedWrapper}>
        <StatusIndicator status={status} />
        {!isDraft && (
          <Typography className={classes.completedValue}>
            {completed}
          </Typography>
        )}
      </div>
    );
  };

  renderType = type => {
    const { classes } = this.props;
    return (
      <SurveyType
        typeIconClass={classes.typeIcon}
        tooltipClass={classes.surveyTypeTooltip}
        type={type}
        isIconStatus
      />
    );
  };

  renderOrganizationUser = () => {
    const { classes, translations, organizationSettings } = this.props;

    return (
      <UserAvatar
        labelClass={classes.organizationUser}
        user={{
          [USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME]:
            organizationSettings.name,
          [USER_BASIC_PERSONAL_INFORMATION.LAST_NAME]: `(${translations.organization})`,
        }}
        caption
        small
        isOrganizationUser
      />
    );
  };

  render() {
    const {
      translations,
      classes,
      surveys,
      surveyGroups,
      allSurveyGroups,
      allUsers,
      auth,
      accessControl,
      dialogs,
      organizationSettings,
      setDialogVisibility,
      pendingSurveys,
      attributesWithQuestions,
      attribute,
      getAttribute,
      clearAttribute,
    } = this.props;
    const {
      isInitialLoad,
      isLoading,
      isReadMore,
      filters,
      ordering,
      isSurveyTypeDialogOpened,
      selectedSurveyType,
      isCreateSurveyOpened,
      isSendSurveyNotificationOpened,
      surveyData,
      predefinedSurveyData,
      search,
      isSurveyTitleDialogOpened,
      isManageSurveyGroupDialogOpened,
      isGroupDialogOpened,
      isGroupView,
      survey,
      surveyGroup,
      isDeleteSurveyGroupOpened,
      isDuplicateGroupOpened,
      isDuplicateSurveyOpened,
      isRemoveFromGroupOpened,
    } = this.state;
    const { forceCompleteSurveyDialogOpened, forceDeleteSurveyDialogOpened } =
      dialogs;
    const enpsAttribute = this.props.attributesWithQuestions.filter(
      attr => attr.type === ATTRIBUTE_TYPES_MAP.ENPS.id
    );
    const surveyTypes = prepareSurveyTypes(translations);
    const dialogSurveyTypes = prepareSurveyTypes(translations, true);
    const surveyViewOptions = prepareSurveyViewOptions(
      translations.surveyViewOptions
    );
    const filterIsActive = hasSelectedFiltersCheck(filters);
    const [
      canViewAllSurveys,
      canManageAllSurveys,
      canViewSurveysCreated,
      canManageSurveysCreated,
    ] = hasAuthPermissionsCheck(
      [
        PERMISSION_CODES.SURVEYS_VIEW_ALL,
        PERMISSION_CODES.SURVEYS_MANAGE_ALL,
        PERMISSION_CODES.SURVEYS_VIEW_CREATED,
        PERMISSION_CODES.SURVEYS_MANAGE_CREATED,
      ],
      accessControl
    );
    const employees = getEmployees(allUsers);
    const associates = getAssociates(allUsers);
    const hasLoadMore = hasNextPage(surveys);
    const hasLoadMoreGroups = hasNextPage(surveyGroups);
    const gridHeaders = prepareSurveysTableHeaders(
      translations.columns,
      !isGroupView,
      canViewAllSurveys
    );
    const gridHeaderActions = prepareSurveysHeaderActions(translations.columns);
    const allAttributes = attributesWithQuestions.filter(
      attr => attr.type !== ATTRIBUTE_TYPES_MAP.ENPS.id
    );
    const surveyType = getItemById(
      surveyTypes,
      selectedSurveyType || SURVEY_TYPES.SELF
    );
    const isNps = [SURVEY_TYPES.NPS, SURVEY_TYPES.ENPS].includes(
      selectedSurveyType
    );
    const isEnpsType = SURVEY_TYPES.ENPS === selectedSurveyType;
    const predefinedSurveyInfo = {
      ...predefinedSurveyData,
      ...(isEnpsType
        ? {
            enpsReviewers: employees,
            [CREATE_SURVEY_FIELD_NAMES.IS_ANONYMOUS]: true,
          }
        : {}),
      ...(isNps ? { attributes: enpsAttribute } : {}),
    };

    return (
      !isInitialLoad && (
        <div>
          <div className={classes.stickyHeader}>
            <div
              className={classNames(classes.descriptionWrapper, {
                [classes.descriptionReadMoreWrapper]: !isReadMore,
              })}
            >
              <ReadLessMore
                translations={translations}
                isReadMore={isReadMore}
                toggleReadMore={this.handleToggleReadMore}
              >
                <Typography variant="body2" className={classes.descriptionItem}>
                  {translations.descriptionPartOne}
                </Typography>
                <Typography variant="body2" className={classes.descriptionItem}>
                  {translations.descriptionPartTwo}
                </Typography>
              </ReadLessMore>
              {!isArrayEmpty(pendingSurveys) && (
                <SurveyActionBar
                  className={classes.pendingSurveysBar}
                  translations={translations.surveysActionBar}
                  surveys={pendingSurveys}
                  onFillInlineSurvey={this.handleFillInlineSurvey}
                />
              )}
            </div>
            <div className={classes.filtersAndPagerContainer}>
              {!isGroupView && (
                <div className={classes.filterSection}>
                  <Filters
                    translations={translations.filters}
                    selectedFilters={filters}
                    filters={prepareSurveysPageFilters(
                      translations,
                      allUsers,
                      canViewAllSurveys
                    )}
                    onApplyFilters={this.handleApplyFilters}
                  />
                </div>
              )}
              <div className={classes.rightSide}>
                <Search
                  className={classes.search}
                  placeholder={translations.searchPlaceholder}
                  value={search}
                  onChange={this.onSearch}
                />
                {canViewAllSurveys && (
                  <SelectField
                    className={classes.actionButton}
                    menuClass={classes.viewMenu}
                    value={isGroupView}
                    options={surveyViewOptions}
                    parser={{ value: 'value', label: 'name' }}
                    onChange={this.handleChangeView}
                    isSearchDisabled
                    shouldRemoveLabel
                  />
                )}
                {(canManageAllSurveys || canManageSurveysCreated) && (
                  <>
                    {isGroupView ? (
                      <CustomButton
                        className={classes.actionButton}
                        type="addRoundedNew"
                        onClick={this.handleOpenGroupDialog}
                      >
                        {translations.addGroupButton}
                      </CustomButton>
                    ) : (
                      <CustomButton
                        className={classes.actionButton}
                        type="addRoundedNew"
                        onClick={this.handleOpenSurveyTypeDialog}
                      >
                        {translations.sendSurvey}
                      </CustomButton>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {!isGroupView &&
            isArray(surveys.results) &&
            !isArrayEmpty(surveys.results) && (
              <GridTable
                className={classes.table}
                translations={translations}
                customHeaderClass={classNames({
                  [classes.readMoreActive]: !isReadMore,
                })}
                initialSort={ordering}
                headerActions={gridHeaderActions}
                headers={gridHeaders}
                hasLoadMore={hasLoadMore}
                rows={prepareSurveysTableData(
                  surveys.results,
                  translations,
                  surveyTypes,
                  allSurveyGroups,
                  canViewAllSurveys,
                  canManageAllSurveys,
                  canViewSurveysCreated,
                  canManageSurveysCreated,
                  this.renderTitle,
                  this.renderType,
                  this.renderStatusAndCompleted,
                  this.renderActions
                )}
                onLoadMore={this.onLoadMore}
                onSort={this.onSort}
              />
            )}
          {isGroupView &&
            isArray(surveyGroups.results) &&
            !isArrayEmpty(surveyGroups.results) && (
              <SurveyGroups
                translations={translations.groupView}
                surveyColumns={[...gridHeaders, ...gridHeaderActions]}
                groups={prepareSurveyGroupsData(
                  surveyGroups.results,
                  translations,
                  surveyTypes,
                  allSurveyGroups,
                  canViewAllSurveys,
                  canManageAllSurveys,
                  canViewSurveysCreated,
                  canManageSurveysCreated,
                  this.renderTitle,
                  this.renderType,
                  this.renderStatusAndCompleted,
                  this.renderActions
                )}
                hasLoadMore={hasLoadMoreGroups}
                canManageAllSurveys={canManageAllSurveys}
                onLoadMore={this.onLoadMore}
                onEditGroupTitle={this.handleOpenGroupDialog}
                onDeleteGroup={this.handleOpenGroupDelete}
                onDuplicateGroup={this.handleOpenGroupDuplicate}
                onCreateSurvey={this.handleOpenSurveyTypeDialog}
                onReorderGroupItems={this.handleReorderGroupItems}
              />
            )}
          <NotificationCard
            {...prepareNoSurveyResultsData(
              translations,
              isLoading,
              isGroupView,
              search || filterIsActive
            )}
            shouldFade={
              !isLoading &&
              ((!isGroupView &&
                isArray(surveys.results) &&
                isArrayEmpty(surveys.results)) ||
                (isGroupView &&
                  isArray(surveyGroups.results) &&
                  isArrayEmpty(surveyGroups.results)))
            }
          />
          <SurveyTypeDialog
            translations={translations.surveyTypeDialog}
            isOpened={isSurveyTypeDialogOpened}
            surveyTypes={dialogSurveyTypes}
            selectedType={selectedSurveyType || SURVEY_TYPES.SELF}
            onSelect={this.handleSelectSurveyType}
            onCancel={this.handleCloseSurveyType}
            onConfirm={this.handleSaveSurveyType}
          />
          <CustomFormDrawer
            customHeaderClass={classes.createSurveyHeader}
            columnContentWrapperClass={classes.columnContent}
            customColumnsClass={classes.columns}
            translations={translations.createSurveyForm}
            titleText={surveyType.name}
            titleIcon={surveyType.icon}
            initialData={prepareCreateSurveyInitialData(
              translations,
              selectedSurveyType,
              surveyType,
              this.renderOrganizationUser,
              organizationSettings.name,
              surveyGroup,
              predefinedSurveyInfo
            )}
            isOpened={isCreateSurveyOpened}
            columns={prepareCreateSurveyColumns(
              translations.createSurveyForm.columns,
              classes,
              translations.defaultEmailMessage,
              selectedSurveyType,
              surveyTypes,
              predefinedSurveyInfo,
              this.handleChangeSurveyType
            )}
            allUsers={allUsers}
            associates={associates}
            employees={employees}
            surveyTypes={surveyTypes}
            attributesWithQuestions={allAttributes}
            attribute={attribute}
            getAttribute={getAttribute}
            clearAttribute={clearAttribute}
            onClose={this.handleCloseCreateSurveyDrawer}
            onSave={this.handleCreateSurveySave}
            hasCancelButton
            isWholeScreen
          />
          {surveyData && isSendSurveyNotificationOpened && (
            <SendSurveyNotificationDialog
              translations={translations.sendSurveyNotificationDialog}
              isOpened={isSendSurveyNotificationOpened}
              surveyData={surveyData}
              currentUser={auth}
              onFillInlineChange={this.handleFillInlineChange}
              onClose={this.handleCloseSurveyNotificationDialog}
              onConfirm={this.handleCreateSurvey}
            />
          )}
          <AlertDialog
            translations={prepareSurveyDeleteTranslations(translations, survey)}
            isOpened={forceDeleteSurveyDialogOpened}
            onClose={() => {
              this.setState({ survey: null }, () => {
                setDialogVisibility({
                  dialogName: 'forceDeleteSurveyDialog',
                  opened: false,
                });
              });
            }}
            onConfirm={this.handleDeleteSurvey}
            isWarning
          />
          <AlertDialog
            translations={translations.forceCompleteDialog}
            isOpened={forceCompleteSurveyDialogOpened}
            onClose={() => {
              this.setState({ survey: null }, () => {
                setDialogVisibility({
                  dialogName: 'forceCompleteSurveyDialog',
                  opened: false,
                });
              });
            }}
            onConfirm={this.handleForceCompleteSurvey}
            isWarning
          />
          <AlertDialog
            translations={translations.surveyGroupDeleteDialog}
            isOpened={isDeleteSurveyGroupOpened}
            onClose={this.handleCloseGroupDelete}
            onConfirm={this.handleDeleteGroup}
            isWarning
          />
          <AlertDialog
            translations={translations.removeFromGroupDialog}
            isOpened={isRemoveFromGroupOpened}
            onClose={this.handleCloseRemoveFromGroupDialog}
            onConfirm={this.handleSaveRemoveFromGroup}
            isWarning
          />
          <AlertDialog
            translations={translations.surveyGroupDuplicateDialog}
            isOpened={isDuplicateGroupOpened}
            onClose={this.handleCloseGroupDuplicate}
            onConfirm={this.handleDuplicateGroup}
          />
          <AlertDialog
            translations={translations.surveyDuplicateDialog}
            isOpened={isDuplicateSurveyOpened}
            onClose={this.handleCloseSurveyDuplicate}
            onConfirm={this.handleSaveSurveyDuplicate}
          />
          <ChooseNameDialog
            translations={translations.renameSurveyDialog}
            title={translations.renameSurveyDialog.title}
            description={translations.renameSurveyDialog.description}
            isOpened={isSurveyTitleDialogOpened}
            initialValue={survey?.title}
            field={SURVEY_TITLE_FIELD}
            onCancel={this.handleToggleSurveyTitleDialog}
            onSave={this.handleChangeSurveyTitle}
          />
          <ChooseNameDialog
            translations={translations.manageGroupDialog}
            title={
              surveyGroup
                ? translations.manageGroupDialog.title.edit
                : translations.manageGroupDialog.title.create
            }
            description={translations.manageGroupDialog.description}
            isOpened={isGroupDialogOpened}
            initialValue={surveyGroup?.title}
            field={SURVEYS_GROUP_FIELD}
            onCancel={this.handleCloseGroupDialog}
            onSave={this.handleSaveGroup}
          />
          <CustomSelectDialog
            translations={translations.manageGroupItemsDialog}
            subTitle={survey?.title}
            isOpened={isManageSurveyGroupDialogOpened}
            options={allSurveyGroups}
            isEdit={!!survey?.group}
            initialValue={survey?.group}
            field={SURVEY_GROUP_FIELD}
            onCancel={this.handleCloseSurveyGroupDialog}
            onSave={this.handleSaveSurveyGroup}
          />
        </div>
      )
    );
  }
}

SurveysPage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  organizationSettings: PropTypes.shape({}).isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  dialogs: PropTypes.object.isRequired,
  allUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allSurveyGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  attributesWithQuestions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  surveyGroups: PropTypes.object.isRequired,
  surveys: PropTypes.object.isRequired,
  attribute: PropTypes.shape({}).isRequired,
  getSurveys: PropTypes.func.isRequired,
  clearSurveys: PropTypes.func.isRequired,
  setDialogVisibility: PropTypes.func.isRequired,
  setIsInlineSurvey: PropTypes.func.isRequired,
  getAllUsers: PropTypes.func.isRequired,
  clearAllUsers: PropTypes.func.isRequired,
  getAttributesWithQuestions: PropTypes.func.isRequired,
  clearAttributesWithQuestions: PropTypes.func.isRequired,
  getTrackingById: PropTypes.func.isRequired,
  getAttribute: PropTypes.func.isRequired,
  clearAttribute: PropTypes.func.isRequired,
  editSurvey: PropTypes.func.isRequired,
  clearSurvey: PropTypes.func.isRequired,
  getSurveyGroups: PropTypes.func.isRequired,
  clearSurveyGroups: PropTypes.func.isRequired,
  getAllSurveyGroups: PropTypes.func.isRequired,
  clearAllSurveyGroups: PropTypes.func.isRequired,
  setGroupSurveys: PropTypes.func.isRequired,
  deleteSurveyFromGroup: PropTypes.func.isRequired,
};

export default withStyles(styles)(SurveysPage);
